import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useBranding } from "@/lib/branding";
import { useAuth } from "@/lib/auth";
import RefundCalculator from "@/components/RefundCalculator";
import ShareAmendmentOffer from "@/components/ShareAmendmentOffer";
import {
  ArrowRight,
  ClipboardCheck,
  CalendarCheck,
  FileCheck2,
  MessageCircle,
  Phone,
  ShieldCheck,
  Wallet,
  Calculator,
  Upload,
  Send,
  FilePenLine,
  Sparkles,
} from "lucide-react";

/**
 * `/clients` — Marketing hub for tax-prep clients (new + returning).
 * Gives them clear paths to: intake form, book appointment, refund status.
 * Distinct from `/client-portal` which is the logged-in status tracker.
 */
export default function Clients() {
  const { branding } = useBranding();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background" data-testid="clients-hub-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="clients-brand-link">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">
                {branding.brand_name?.[0]}
              </div>
            )}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">Home</Link>
            <Link to="/become-a-tax-pro" className="hidden md:inline underline text-muted-foreground hover:text-foreground">Become a Tax Pro</Link>
            <Link to="/tax-pros" className="hidden md:inline underline text-muted-foreground hover:text-foreground">Tax Pros</Link>
            {user ? (
              <Button asChild size="sm" data-testid="clients-nav-portal"><Link to="/client-portal">My return status</Link></Button>
            ) : (
              <Button asChild size="sm" variant="outline" data-testid="clients-nav-login"><Link to="/login">Sign in</Link></Button>
            )}
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse at 20% 0%, rgba(46,79,184,0.22), transparent 55%), radial-gradient(ellipse at 100% 100%, rgba(214,138,61,0.18), transparent 55%), linear-gradient(180deg, #0f172a 0%, #1e293b 100%)",
        }}
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-white text-xs uppercase tracking-widest mb-6">
            <ShieldCheck size={14} /> For our tax-prep clients · Season 2025
          </div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] text-white mb-6">
            File with confidence.<br />
            <span className="italic text-accent">100% virtual</span>, all 50 states.
          </h1>
          <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-8">
            Whether you're a returning client or filing with us for the first time, everything you need to get started is right here.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-white/90" data-testid="clients-cta-intake">
              <Link to="/intake">Start your intake form <ArrowRight size={16} className="ml-2" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10" data-testid="clients-cta-book">
              <Link to="/book"><CalendarCheck size={16} className="mr-2" /> Book an appointment</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* THREE TABS: Refund Calculator · Intake Form · Ready to File */}
      <section className="border-t border-border" data-testid="clients-tabs-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">Everything you need — in one place</div>
            <h2 className="font-serif text-4xl md:text-5xl">
              Fastest path to your <span className="italic text-primary">biggest refund.</span>
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Estimate your refund, complete your intake, then hand it off — ready to file with a real preparer.
            </p>
          </div>

          {/* AMENDMENT BANNER — friendly, hard-to-miss shortcut for the *other* audience */}
          <Link
            to="/amend"
            className="block mb-8 rounded-lg border-2 border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors p-5 md:p-6"
            data-testid="clients-amend-banner"
          >
            <div className="flex items-center gap-4 flex-wrap">
              <div className="h-12 w-12 rounded-md bg-primary text-primary-foreground grid place-items-center shrink-0">
                <FilePenLine size={22} />
              </div>
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles size={12} className="text-primary" />
                  <span className="label-caps text-[10px] text-primary">Already filed? Need a fix?</span>
                </div>
                <div className="font-serif text-xl md:text-2xl leading-tight">
                  Amend a prior return — <span className="text-primary">$175 through Oct 15</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Corrected W-2, missed credit, wrong filing status? We fix it. Any year 2022-2026. Regular price $275 after Oct 15.
                </p>
              </div>
              <div className="text-primary font-medium text-sm flex items-center gap-1 shrink-0">
                Start amendment <ArrowRight size={14} />
              </div>
            </div>
          </Link>

          <Tabs defaultValue="calculator" className="w-full">
            <TabsList className="w-full h-auto grid grid-cols-1 sm:grid-cols-3 gap-2 bg-transparent p-0 mb-8" data-testid="clients-tabs-list">
              <TabsTrigger
                value="calculator"
                className="flex-col h-auto p-5 border-2 border-border data-[state=active]:border-primary data-[state=active]:bg-primary/5 data-[state=active]:shadow-none rounded-lg gap-2"
                data-testid="clients-tab-calculator"
              >
                <div className="flex items-center gap-2 text-primary">
                  <Calculator size={18} />
                  <span className="label-caps text-[10px]">Step 1</span>
                </div>
                <div className="font-serif text-lg">Refund Calculator</div>
                <div className="text-[11px] text-muted-foreground font-normal">Ballpark your refund in 30 seconds</div>
              </TabsTrigger>
              <TabsTrigger
                value="intake"
                className="flex-col h-auto p-5 border-2 border-border data-[state=active]:border-primary data-[state=active]:bg-primary/5 data-[state=active]:shadow-none rounded-lg gap-2"
                data-testid="clients-tab-intake"
              >
                <div className="flex items-center gap-2 text-primary">
                  <ClipboardCheck size={18} />
                  <span className="label-caps text-[10px]">Step 2</span>
                </div>
                <div className="font-serif text-lg">Intake Form</div>
                <div className="text-[11px] text-muted-foreground font-normal">Tell us about your return · ~10 min</div>
              </TabsTrigger>
              <TabsTrigger
                value="ready"
                className="flex-col h-auto p-5 border-2 border-border data-[state=active]:border-primary data-[state=active]:bg-primary/5 data-[state=active]:shadow-none rounded-lg gap-2"
                data-testid="clients-tab-ready"
              >
                <div className="flex items-center gap-2 text-primary">
                  <Send size={18} />
                  <span className="label-caps text-[10px]">Step 3</span>
                </div>
                <div className="font-serif text-lg">Ready to File</div>
                <div className="text-[11px] text-muted-foreground font-normal">Upload · submit · book</div>
              </TabsTrigger>
            </TabsList>

            {/* Tab 1: Refund Calculator */}
            <TabsContent value="calculator" data-testid="clients-tab-panel-calculator">
              <div className="grid lg:grid-cols-5 gap-8 items-start">
                <div className="lg:col-span-2">
                  <h3 className="font-serif text-3xl mb-3">See what you could get back.</h3>
                  <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
                    A quick TY 2025 federal estimate — plug in your W-2 wages, withholding, and any dependents. The calculator handles standard deduction, Child Tax Credit (with the refundable ACTC), and Earned Income Tax Credit.
                  </p>
                  <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
                    Not a final number — every credit, deduction, and phaseout gets confirmed by a real preparer once you submit your intake.
                  </p>
                  <div className="rounded-md border border-primary/30 bg-primary/5 p-4 text-xs text-muted-foreground">
                    <strong className="text-foreground">Heads up:</strong> Ignores state tax, itemized deductions, AMT, and investment-income limits. Real refunds can differ.
                  </div>
                </div>
                <div className="lg:col-span-3">
                  <RefundCalculator />
                </div>
              </div>
            </TabsContent>

            {/* Tab 2: Intake Form summary */}
            <TabsContent value="intake" data-testid="clients-tab-panel-intake">
              <div className="grid lg:grid-cols-5 gap-8 items-start">
                <div className="lg:col-span-2">
                  <h3 className="font-serif text-3xl mb-3">Complete your intake.</h3>
                  <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
                    Tell us about your household, income, deductions, and any life changes since last year. Save & finish later on any device. Takes about 10 minutes.
                  </p>
                  <Button asChild size="lg" className="w-full sm:w-auto mb-3" data-testid="clients-intake-start-btn">
                    <Link to="/intake">Start intake form <ArrowRight size={16} className="ml-2" /></Link>
                  </Button>
                  <div className="text-xs text-muted-foreground">
                    Already started?{" "}
                    <Link to="/client-portal" className="underline text-primary">Check your status →</Link>
                  </div>
                </div>
                <div className="lg:col-span-3">
                  <div className="border border-border rounded-lg bg-card p-6 lg:p-8">
                    <div className="label-caps mb-4 text-primary">What we'll ask</div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                      {INTAKE_SECTIONS.map((s, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm" data-testid={`clients-intake-section-${i}`}>
                          <div className="h-5 w-5 rounded-full bg-primary/10 text-primary text-[10px] font-medium grid place-items-center shrink-0 mt-0.5">
                            {i + 1}
                          </div>
                          <div>
                            <div className="font-medium">{s.title}</div>
                            <div className="text-xs text-muted-foreground leading-snug">{s.body}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tab 3: Ready to File */}
            <TabsContent value="ready" data-testid="clients-tab-panel-ready">
              <div className="grid lg:grid-cols-5 gap-8 items-start">
                <div className="lg:col-span-2">
                  <h3 className="font-serif text-3xl mb-3">Ready to file.</h3>
                  <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
                    You've estimated your refund and answered our questions — the last step is handing your paperwork off and locking in a filing appointment.
                  </p>
                  <div className="rounded-md border border-primary/30 bg-primary/5 p-4 text-xs text-muted-foreground">
                    <strong className="text-foreground">Fastest way to funded:</strong> Upload every document that applies to you upfront — missing W-2s or IDs are the #1 reason returns get delayed.
                  </div>
                </div>
                <div className="lg:col-span-3 space-y-4">
                  <ReadyStep
                    n="1"
                    icon={<Upload size={18} />}
                    title="Upload your tax documents"
                    body="W-2, 1099, IDs, 1098-T, 1095, mortgage forms, self-employment receipts — all the paperwork that applies to your situation. Attached inside the intake form; PDF, PNG, JPG up to 20 MB each."
                    cta="Upload documents"
                    to="/intake#uploads"
                    testid="clients-ready-upload"
                  />
                  <ReadyStep
                    n="2"
                    icon={<Send size={18} />}
                    title="Submit to your preparer"
                    body="Sign the intake, hit submit, and we'll get an access code back to you so you can track your return's progress at any time."
                    cta="Submit intake"
                    to="/intake"
                    testid="clients-ready-submit"
                    highlight
                  />
                  <ReadyStep
                    n="3"
                    icon={<CalendarCheck size={18} />}
                    title="Book a filing appointment"
                    body="Pick a video slot that works for you. We'll walk through your return live, answer questions, and e-file the same session."
                    cta="See available times"
                    to="/book"
                    testid="clients-ready-book"
                  />
                  <ReadyStep
                    n="4"
                    icon={<FileCheck2 size={18} />}
                    title="Track your refund"
                    body="After we file, log in to your client portal to see IRS acceptance and estimated deposit date — all in one place."
                    cta={user ? "Open my portal" : "Sign in to portal"}
                    to={user ? "/client-portal" : "/login?next=/client-portal"}
                    testid="clients-ready-portal"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* WHAT WE FILE */}
      <section className="border-t border-border bg-secondary/40">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">What we file</div>
            <h2 className="font-serif text-4xl md:text-5xl">Individual & business returns.</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
              We handle everything from a simple W-2 to complex Schedule C, rental income, small business returns, and multi-state filings.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {SERVICES.map((s, i) => {
              const inner = (
                <>
                  <div className="h-11 w-11 rounded-md bg-primary/10 text-primary grid place-items-center shrink-0">
                    {s.icon}
                  </div>
                  <div>
                    <h3 className="font-serif text-lg">{s.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                    {s.cta && <div className="text-xs text-primary underline mt-2">{s.cta} →</div>}
                  </div>
                </>
              );
              const cls = "p-5 rounded-lg border border-border bg-card flex gap-4 transition-colors hover:border-primary/40";
              return s.href ? (
                <Link key={i} to={s.href} className={cls} data-testid={`clients-service-${i}`}>{inner}</Link>
              ) : (
                <div key={i} className={cls} data-testid={`clients-service-${i}`}>{inner}</div>
              );
            })}
          </div>

          {/* Share the amendment offer — friendly, non-pushy referral card */}
          <div className="mt-8 max-w-xl mx-auto">
            <ShareAmendmentOffer compact />
          </div>
        </div>
      </section>

      {/* CONTACT / REASSURANCE */}
      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16 text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-4">
            Questions? <span className="italic text-primary">Reach out.</span>
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Message us anytime — we usually reply the same day. Or ask our 24/7 AI helper (bottom-right corner) if you just need a quick answer.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild variant="outline" data-testid="clients-cta-message">
              <a href="mailto:dimeka@dynamicfinancials.online">
                <MessageCircle size={14} className="mr-2" /> Email us
              </a>
            </Button>
            <Button asChild variant="outline" data-testid="clients-cta-phone">
              <Link to="/book"><Phone size={14} className="mr-2" /> Schedule a call</Link>
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-3">
          <div>Powered by <span className="text-foreground">{branding.brand_name}</span></div>
          <div className="flex gap-4">
            <Link to="/become-a-tax-pro" className="underline hover:text-foreground">Become a Tax Pro</Link>
            <Link to="/tax-pros" className="underline hover:text-foreground">For Tax Pros</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ReadyStep({ n, icon, title, body, cta, to, testid, highlight }) {
  return (
    <div
      className={`p-5 rounded-lg border-2 ${highlight ? "border-primary bg-primary/5" : "border-border bg-card"} flex flex-col sm:flex-row gap-4`}
      data-testid={testid}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className={`h-10 w-10 rounded-md shrink-0 grid place-items-center ${highlight ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="label-caps text-[10px] text-primary">Step {n}</span>
          </div>
          <h4 className="font-serif text-xl mb-1">{title}</h4>
          <p className="text-xs text-muted-foreground leading-relaxed">{body}</p>
        </div>
      </div>
      <Button asChild variant={highlight ? "default" : "outline"} size="sm" className="self-start sm:self-center shrink-0" data-testid={`${testid}-btn`}>
        <Link to={to}>{cta} <ArrowRight size={12} className="ml-2" /></Link>
      </Button>
    </div>
  );
}

const INTAKE_SECTIONS = [
  { title: "Personal info", body: "Name, DOB, SSN, address, phone, email" },
  { title: "Filing status", body: "Single, MFJ, MFS, HOH, or QW" },
  { title: "Dependents", body: "Kids and anyone else you support" },
  { title: "Income sources", body: "W-2s, 1099s, self-employment, rental, investment" },
  { title: "Deductions & credits", body: "Itemize? Child care, education, EV, energy" },
  { title: "Document uploads", body: "IDs, W-2s, 1099s, and any supporting paperwork" },
  { title: "Refund delivery", body: "RA card, direct deposit, or check" },
  { title: "Sign & submit", body: "E-signature + optional cash-advance application" },
];

const SERVICES = [
  { icon: <FileCheck2 size={22} />, title: "Form 1040 — Individual Returns", body: "W-2, 1099, self-employment, rental, retirement, credits (EITC, CTC, AOTC), and multi-state filings." },
  { icon: <ClipboardCheck size={22} />, title: "Form 1120 — C-Corp Returns", body: "Corporate returns including depreciation, DRD, schedules L / M-1 / M-2, and quarterly estimated tax planning." },
  { icon: <Wallet size={22} />, title: "Refund Advance (Pre-approved)", body: "Get up to $6,000 within 24 hours of IRS acceptance — no credit check, no impact on your refund amount." },
  { icon: <ShieldCheck size={22} />, title: "Amendments (Form 1040-X)", body: "Missed a credit? Got a corrected W-2? File your amendment starting at $175 through Oct 15 — regular price $275.", href: "/amend", cta: "Start amendment" },
];
